import { Link } from "react-router-dom";
import { Shield, Mail, Trash2, ScanLine, UserCheck, FileText } from "lucide-react";

const EFFECTIVE_DATE = "February 1, 2026";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">PRIVACY POLICY</p>
      <h1 className="cm-heading text-4xl sm:text-5xl lg:text-6xl mt-3">
        Your photo, <span className="text-copper">your privacy</span>.
      </h1>
      <p className="mt-4 text-sm text-gray-500">
        Effective date: <time dateTime="2026-02-01">{EFFECTIVE_DATE}</time>
      </p>
      <p className="mt-6 text-gray-300 leading-relaxed">
        HairMaxxing Pro takes biometric privacy seriously. This policy explains
        exactly what we collect when you submit a photo, why we collect it,
        and how quickly it&rsquo;s deleted.
      </p>

      <section aria-labelledby="collect" className="mt-12">
        <h2 id="collect" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <ScanLine className="w-5 h-5 text-copper" aria-hidden="true" />
          What we collect
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          When you upload a photo and tap &ldquo;Analyze haircut,&rdquo; our AI
          analyzes <strong className="text-platinum">facial-geometry
          measurements</strong> derived from the photo &mdash; the shape of
          the head, hairline, ear position, and the haircut itself &mdash; in
          order to identify the style and recommend matching products. We do
          not generate or store a faceprint, embedding, or biometric
          identifier that could be used to identify you in another photo or
          database.
        </p>
      </section>

      <section aria-labelledby="why" className="mt-10">
        <h2 id="why" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <FileText className="w-5 h-5 text-copper" aria-hidden="true" />
          Why we collect it
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          Solely to generate your hairstyle and haircut recommendations,
          including the product routine and the script to read to your
          barber. No other purpose. We do not use it for advertising, model
          training, profile building, or sharing with third parties.
        </p>
      </section>

      <section aria-labelledby="retention" className="mt-10 cm-card p-6 border-copper/40">
        <h2 id="retention" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Trash2 className="w-5 h-5 text-copper" aria-hidden="true" />
          Retention &mdash; we delete fast (unless you opt in)
        </h2>
        <ul className="mt-4 space-y-3 text-gray-300 leading-relaxed list-disc list-inside marker:text-copper">
          <li>
            <strong className="text-platinum">By default</strong>, your uploaded
            photo is deleted from our storage{" "}
            <strong className="text-platinum">immediately after your
            results are generated</strong> (typically within a few seconds).
          </li>
          <li>
            <strong className="text-platinum">Optional opt-in:</strong> on the
            upload screen you may tick &ldquo;Save photo to my account&rdquo;
            to keep the photo tied to your account so it shows up in your
            History across all devices you log into. The photo is stored
            only for that purpose and is{" "}
            <strong className="text-platinum">never used to extract or
            compute biometric identifiers</strong> (no facial-geometry vector,
            no embedding, no faceprint is created or stored, ever).
          </li>
          <li>
            You can delete any saved photo at any time from History or the
            Scan Result page &mdash; this purges it from our storage
            permanently and the corresponding scan record then shows
            &ldquo;Photo purged.&rdquo; Deleting your account also deletes
            every saved photo.
          </li>
          <li>
            Any derived facial-geometry data is discarded at analysis time
            and never written to our database &mdash; regardless of whether
            you choose to save the photo.
          </li>
          <li>
            We never sell, rent, or share your photo or any derived data
            with third parties.
          </li>
          <li>
            What we always keep: your consent record (timestamp, consent text
            version, anonymous session identifier) and the text-only
            hairstyle analysis (haircut name, product routine, barber
            script). These contain no biometric information.
          </li>
        </ul>
      </section>

      <section aria-labelledby="thirdparty" className="mt-10">
        <h2 id="thirdparty" className="cm-heading text-2xl text-platinum">
          Third-party AI provider
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          The image analysis is performed by an industry-standard vision AI
          provider accessed through the Emergent platform. The provider is
          contractually prohibited from retaining your image or training on
          it. Once the analysis returns, the image is dropped from memory
          on our side and deleted from storage.
        </p>
      </section>

      <section aria-labelledby="rights" className="mt-10">
        <h2 id="rights" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <UserCheck className="w-5 h-5 text-copper" aria-hidden="true" />
          Your rights
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          You can email us at any time to:
        </p>
        <ul className="mt-3 space-y-2 text-gray-300 list-disc list-inside marker:text-copper">
          <li>Ask what data we hold on you.</li>
          <li>Request a copy of your scan history (text only).</li>
          <li>Request deletion of your account and all associated data.</li>
          <li>Withdraw consent for biometric analysis going forward.</li>
        </ul>
        <p className="mt-4 text-gray-300 leading-relaxed">
          You can also delete your account directly from the{" "}
          <Link to="/account" className="text-copper underline hover:text-copper/80">
            Account page
          </Link>
          .
        </p>
      </section>

      <section aria-labelledby="contact" className="mt-10 cm-card p-6 border-copper/30">
        <h2 id="contact" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Mail className="w-5 h-5 text-copper" aria-hidden="true" />
          Contact
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          Questions about this policy or your data?
        </p>
        <a
          href="mailto:support@hairmaxxingpro.com?subject=Privacy%20Question"
          className="cm-btn-primary inline-flex mt-5"
          data-testid="privacy-contact-link"
        >
          support@hairmaxxingpro.com
        </a>
      </section>

      <section aria-labelledby="changes" className="mt-10">
        <h2 id="changes" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Shield className="w-5 h-5 text-copper" aria-hidden="true" />
          Changes to this policy
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          If we change the way we collect, use, or retain photos or
          facial-geometry data, we will update this page and update the
          consent text shown before each scan. By scanning again after a
          change, you accept the new version.
        </p>
      </section>

      <div className="mt-12 flex flex-wrap gap-4">
        <Link to="/" className="cm-btn-secondary inline-flex" data-testid="privacy-back-home">
          Back to home
        </Link>
        <Link to="/terms" className="cm-btn-secondary inline-flex" data-testid="privacy-to-terms">
          Read Terms of Service
        </Link>
      </div>
    </div>
  );
}
